/**
 * Pure heuristic for detecting "pure m2m join tables" — link tables like
 * `order_items` that connect two real entities and carry nothing else.
 *
 * Per spec Behavior 8 (lines 322-334) a table is a pure-m2m join when ALL
 * of the following hold (this file owns the 4-rule heuristic):
 *
 *  1. Has exactly 2 outbound FKs.
 *  2. Has no inbound FKs (no other table references it).
 *  3. PK is composite of the two FK columns (OR a surrogate id + UNIQUE on
 *     the two FK columns).
 *  4. Non-FK column count ≤ 2 and those columns are timestamps /
 *     surrogate id.
 *
 * The MetaInfo hint (`joinTableHint: 'collapse' | 'expand' | 'auto'`)
 * overrides the heuristic when set to `'collapse'` or `'expand'`.
 *
 * No React, no IPC — feed it already-introspected metadata. The integration
 * point (running the probe at schema-load or at EP6 drill-time) lives in the
 * call sites (RelatedDataMiniGrid wiring + Slice 6's schema-store cache).
 */

import type { JoinTableHint } from './joinTableHintTypes';

export type JoinTableProbeColumn = {
  readonly name: string;
  /** Lower-cased type family — 'int' | 'bigint' | 'timestamp' | 'datetime' | … */
  readonly dataType: string;
  readonly isPrimaryKey?: boolean;
  /**
   * True when this column is an auto-increment / IDENTITY surrogate id (the
   * heuristic still counts it as "surrogate" in rule 4 even when it's the PK).
   */
  readonly isIdentity?: boolean;
};

export type JoinTableProbe = {
  /** Names of columns that are part of OUTBOUND foreign keys on this table. */
  readonly outboundFkColumns: ReadonlyArray<string>;
  /** How many distinct outbound FK constraints this table carries. */
  readonly outboundFkCount: number;
  /**
   * How many other tables reference this table (inbound FKs). A pure m2m
   * join is a leaf — nothing should reference it.
   */
  readonly inboundFkCount: number;
  /** Ordered PK column names. Empty array when no PK exists. */
  readonly primaryKeyColumns: ReadonlyArray<string>;
  /**
   * Names of UNIQUE-constrained column sets on the table (each set is a
   * separate UNIQUE index). Used to recognise surrogate-id m2m: id PK +
   * UNIQUE (fk1, fk2).
   */
  readonly uniqueColumnSets: ReadonlyArray<ReadonlyArray<string>>;
  readonly columns: ReadonlyArray<JoinTableProbeColumn>;
};

const SURROGATE_NAME_RE = /^(id|pk|key|uuid|guid)$/i;
const TIMESTAMP_NAME_RE = /_(at|on|date|time|timestamp)$/i;
const TIMESTAMP_TYPE_RE = /(timestamp|datetime|date|time)/i;

function eqSet(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b.map((s) => s.toLowerCase()));
  return a.every((x) => sb.has(x.toLowerCase()));
}

function isSurrogateIdColumn(col: JoinTableProbeColumn): boolean {
  if (col.isIdentity) return true;
  if (SURROGATE_NAME_RE.test(col.name)) {
    // Tighten further: only int-like types qualify so we don't accidentally
    // tag a `key TEXT` business-data column as surrogate.
    const t = col.dataType.toLowerCase();
    if (/(int|serial|bigserial|number|integer|bigint|smallint)/.test(t)) {
      return true;
    }
  }
  return false;
}

function isTimestampColumn(col: JoinTableProbeColumn): boolean {
  if (TIMESTAMP_NAME_RE.test(col.name)) return true;
  if (TIMESTAMP_TYPE_RE.test(col.dataType)) return true;
  return false;
}

/** Rule 3 — PK shape: composite of the 2 FK cols OR surrogate id + UNIQUE(fk1,fk2). */
function pkShapeMatches(probe: JoinTableProbe): boolean {
  const fks = probe.outboundFkColumns;
  if (fks.length !== 2) return false;
  const pk = probe.primaryKeyColumns;

  // Composite PK is exactly the two FK columns.
  if (pk.length === 2 && eqSet(pk, fks)) return true;

  // Surrogate id PK + UNIQUE(fk1, fk2).
  if (pk.length === 1) {
    const pkCol = probe.columns.find(
      (c) => c.name.toLowerCase() === pk[0].toLowerCase(),
    );
    if (pkCol && isSurrogateIdColumn(pkCol)) {
      const hasUniqueFkPair = probe.uniqueColumnSets.some((set) =>
        eqSet(set, fks),
      );
      if (hasUniqueFkPair) return true;
    }
  }

  return false;
}

/** Rule 4 — non-FK column count ≤ 2 AND each non-FK col is a timestamp / surrogate id.
 *
 * The surrogate-id PK case (e.g. `id` on an `id + UNIQUE(fk1, fk2)` table)
 * is allowed in Rule 3 — so we permit ≤ 2 NON-SURROGATE non-FK columns and
 * cap the cumulative non-FK column noise at 3 (id + two timestamps is the
 * canonical link-with-timestamps shape).
 */
function nonFkColumnsAreNoise(probe: JoinTableProbe): boolean {
  const fkSet = new Set(probe.outboundFkColumns.map((c) => c.toLowerCase()));
  const nonFkCols = probe.columns.filter(
    (c) => !fkSet.has(c.name.toLowerCase()),
  );
  // Each non-FK column must be either a surrogate-id (allowed by Rule 3)
  // or a timestamp. Non-surrogate, non-timestamp business columns kill the
  // collapse.
  if (!nonFkCols.every((c) => isSurrogateIdColumn(c) || isTimestampColumn(c))) {
    return false;
  }
  // Cap non-surrogate noise at 2 (the spec's ≤ 2 cap). A surrogate id is
  // not noise per Rule 3.
  const nonSurrogateNoise = nonFkCols.filter(
    (c) => !isSurrogateIdColumn(c),
  );
  return nonSurrogateNoise.length <= 2;
}

/**
 * Return true when the table looks like a pure m2m join per the 4-rule
 * heuristic. Pure function — exposed for unit tests.
 */
export function isPureM2mJoinByHeuristic(probe: JoinTableProbe): boolean {
  // Rule 1 — exactly 2 outbound FKs.
  if (probe.outboundFkCount !== 2) return false;
  if (probe.outboundFkColumns.length !== 2) return false;
  // Rule 2 — no inbound FKs.
  if (probe.inboundFkCount !== 0) return false;
  // Rule 3 — PK shape.
  if (!pkShapeMatches(probe)) return false;
  // Rule 4 — non-FK columns are noise.
  if (!nonFkColumnsAreNoise(probe)) return false;
  return true;
}

/**
 * Decide whether a table should render collapsed in the breadcrumb chip-row.
 *
 * Priority (spec line 334 + plan line 257):
 *  - `hint === 'collapse'` → always collapse (user override).
 *  - `hint === 'expand'`   → never collapse (user override).
 *  - `hint === 'auto'`/undefined → fall back to the 4-rule heuristic.
 *
 * Exported as the single decision function so call sites (mini-grid drill +
 * future schema-cache pass) all agree.
 */
export function shouldCollapseJoinTable(
  probe: JoinTableProbe,
  hint: JoinTableHint | undefined,
): boolean {
  if (hint === 'collapse') return true;
  if (hint === 'expand') return false;
  return isPureM2mJoinByHeuristic(probe);
}
